export type ProvenanceViolation = {
  packageName: string;
  dependencyType: string;
  reason: string;
};

export type ProvenancePolicy = {
  pinnedDependencies?: Record<string, string>;
  allowedConsumers?: Partial<Record<string, string[]>>;
};

const ALLOWED_PROTOCOLS = new Set(["npm:", "workspace:", "patch:"]);
const FORBIDDEN_PATTERNS = [
  /^git\+|^git:/i,
  /^file:/i,
  /^link:/i,
  /^portal:/i,
  /^https?:\/\//i,
  /^github:/i,
];

function unwrapPatchSpecifier(specifier: string): string {
  const patchBody = specifier.slice("patch:".length);
  const protocolSeparatorIndex = patchBody.indexOf("#");

  if (protocolSeparatorIndex === -1) {
    return patchBody;
  }

  return patchBody.slice(0, protocolSeparatorIndex);
}

function isAllowedSpecifier(specifier: string): boolean {
  if (specifier.startsWith("@")) {
    return !FORBIDDEN_PATTERNS.some((pattern) => pattern.test(specifier));
  }

  if (specifier.startsWith("patch:")) {
    return isAllowedSpecifier(unwrapPatchSpecifier(specifier));
  }

  if (
    [...ALLOWED_PROTOCOLS].some((protocol) => specifier.startsWith(protocol))
  ) {
    return true;
  }

  return !FORBIDDEN_PATTERNS.some((pattern) => pattern.test(specifier));
}

/**
 * Validates that a workspace package depends only on approved dependency sources.
 */
export function validateDependencyProvenance(
  packageName: string,
  dependencies: Record<string, string> | undefined,
  dependencyType: string,
  policy: ProvenancePolicy = {},
): ProvenanceViolation[] {
  if (!dependencies) {
    return [];
  }

  const violations: ProvenanceViolation[] = [];

  for (const [name, specifier] of Object.entries(dependencies)) {
    const pinnedSpecifier = policy.pinnedDependencies?.[name];
    const allowedConsumers = policy.allowedConsumers?.[name];

    if (allowedConsumers && !allowedConsumers.includes(packageName)) {
      violations.push({
        dependencyType,
        packageName,
        reason: `${name} may only be declared in ${allowedConsumers.join(", ")}, found in ${packageName}`,
      });
    }

    if (pinnedSpecifier && specifier !== pinnedSpecifier) {
      violations.push({
        dependencyType,
        packageName,
        reason: `${name} must be pinned to ${pinnedSpecifier}, found ${specifier}`,
      });
      continue;
    }

    if (isAllowedSpecifier(specifier)) {
      continue;
    }

    violations.push({
      dependencyType,
      packageName,
      reason: `${name}@${specifier} uses an unsupported source`,
    });
  }

  return violations;
}
